import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  MapPinIcon, 
  CurrencyEuroIcon, 
  UsersIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const UpcomingProjects = () => {
  const [selectedProject, setSelectedProject] = useState(6); // Prague Historic Apartments
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [zoomedImage, setZoomedImage] = useState(null);

  const nextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % projects[selectedProject].gallery.length);
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) => (prev - 1 + projects[selectedProject].gallery.length) % projects[selectedProject].gallery.length);
  };

  const projects = [
    {
      id: 1,
      title: "Coastal Lighthouse Retreat",
      location: "Cornwall, England",
      townName: "Cornwall",
      icon: "🏰",
      description: "Historic lighthouse converted into luxury rental property with panoramic ocean views. This unique property combines maritime heritage with modern luxury amenities, offering guests an unforgettable experience.",
      image: "https://images.unsplash.com/photo-1641218356040-ccc36cbef1da",
      gallery: [
        "https://images.unsplash.com/photo-1641218356040-ccc36cbef1da",
        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4"
      ],
      totalInvestment: "€2,400,000",
      nftsRequired: 600,
      expectedReturn: "20%",
      projectDuration: "18 months",
      status: "Filling Fast",
      highlights: [
        "Historic 19th-century lighthouse",
        "360° ocean views",
        "Luxury renovation planned",
        "High-end rental market"
      ],
      progress: 75
    },
    {
      id: 2,
      title: "Scandinavian Lake Houses",
      location: "Lapland, Finland",
      townName: "Lapland",
      icon: "🏔️",
      description: "Premium eco-friendly cabins with modern amenities in pristine Nordic wilderness. These sustainable retreats offer guests the perfect base for Northern Lights viewing and authentic Scandinavian experiences.",
      image: "https://images.unsplash.com/photo-1554114777-4039b6664977",
      gallery: [
        "https://images.unsplash.com/photo-1554114777-4039b6664977",
        "https://images.unsplash.com/photo-1578662996442-48f60103fc96"
      ],
      totalInvestment: "€3,200,000",
      nftsRequired: 800,
      expectedReturn: "22%",
      projectDuration: "24 months",
      status: "Starting Soon",
      highlights: [
        "Sustainable architecture",
        "Northern Lights viewing",
        "Premium eco-tourism",
        "Year-round rental potential"
      ],
      progress: 45
    },
    {
      id: 3,
      title: "Mediterranean Villa Collection",
      location: "Costa del Sol, Spain",
      townName: "Costa del Sol",
      icon: "🏖️",
      description: "Luxury villas with private pools and sea views in premium Spanish coastal region. Each villa features contemporary Mediterranean design with direct beach access and world-class amenities.",
      image: "https://images.unsplash.com/photo-1709747596819-aae29d7bcc27",
      gallery: [
        "https://images.unsplash.com/photo-1709747596819-aae29d7bcc27",
        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4"
      ],
      totalInvestment: "€4,800,000",
      nftsRequired: 1200,
      expectedReturn: "20%",
      projectDuration: "30 months",
      status: "Planning Phase",
      highlights: [
        "Prime coastal location",
        "Private pools & gardens",
        "Luxury amenities",
        "High rental demand"
      ],
      progress: 20
    },
    {
      id: 4,
      title: "Alpine Ski Resort Chalets",
      location: "Swiss Alps, Switzerland",
      townName: "Swiss Alps",
      icon: "🎿",
      description: "Exclusive ski-in/ski-out chalets in world-renowned Alpine resort destination. These premium mountain retreats offer unparalleled access to pristine slopes and luxury alpine hospitality.",
      image: "https://images.unsplash.com/photo-1649520469727-6928ab22ad31",
      gallery: [
        "https://images.unsplash.com/photo-1649520469727-6928ab22ad31",
        "https://images.unsplash.com/photo-1554114777-4039b6664977"
      ],
      totalInvestment: "€6,000,000",
      nftsRequired: 1500,
      expectedReturn: "23%",
      projectDuration: "36 months",
      status: "Coming Soon",
      highlights: [
        "Ski-in/ski-out access",
        "Luxury mountain chalets",
        "Premium resort location",
        "Year-round income potential"
      ],
      progress: 5
    },
    {
      id: 5,
      title: "Coastal Multiplex Complex",
      location: "Algarve, Portugal",
      townName: "Algarve",
      icon: "🌊",
      description: "Modern multiplex residential complex with sea views and resort amenities. This innovative development combines contemporary architecture with traditional Portuguese charm and stunning ocean vistas.",
      image: "https://images.pexels.com/photos/7598668/pexels-photo-7598668.jpeg",
      gallery: [
        "https://images.pexels.com/photos/7598668/pexels-photo-7598668.jpeg",
        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4"
      ],
      totalInvestment: "€5,600,000",
      nftsRequired: 1400,
      expectedReturn: "24%",
      projectDuration: "28 months",
      status: "Development Ready",
      highlights: [
        "Sea view apartments",
        "Resort-style amenities",
        "Prime tourist location",
        "Multiple revenue streams"
      ],
      progress: 30
    },
    {
      id: 6,
      title: "Tuscan Vineyard Estate",
      location: "Tuscany, Italy",
      townName: "Tuscany",
      icon: "🍇",
      description: "Historic vineyard estate with luxury accommodations and wine production facilities. This authentic Italian estate combines centuries-old winemaking traditions with modern luxury hospitality and breathtaking countryside views.",
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4",
      gallery: [
        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4",
        "https://images.unsplash.com/photo-1641218356040-ccc36cbef1da"
      ],
      totalInvestment: "€7,200,000",
      nftsRequired: 1800,
      expectedReturn: "21%",
      projectDuration: "42 months",
      status: "Planning Phase",
      highlights: [
        "Historic wine estate",
        "Premium wine production",
        "Luxury agritourism",
        "Established vineyard operations"
      ],
      progress: 15
    },
    {
      id: 7,
      title: "Prague Historic Apartments",
      location: "Prague, Czech Republic",
      townName: "Prague",
      icon: "🏛️",
      description: "Restored historic apartments in Prague's Old Town with modern luxury amenities. These carefully preserved heritage properties offer guests authentic Central European charm with contemporary comfort in a UNESCO World Heritage setting.",
      image: "https://images.unsplash.com/photo-1541849546-216549ae216d",
      gallery: [
        "https://images.unsplash.com/photo-1541849546-216549ae216d",
        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4"
      ],
      totalInvestment: "€3,800,000",
      nftsRequired: 950,
      expectedReturn: "19%",
      projectDuration: "24 months",
      status: "Coming Soon",
      highlights: [
        "UNESCO World Heritage location",
        "Historic architecture preservation",
        "Premium city center location",
        "High tourism demand"
      ],
      progress: 25
    },
    {
      id: 8,
      title: "Norwegian Fjord Retreat",
      location: "Geiranger, Norway",
      townName: "Geiranger",
      icon: "⛰️",
      description: "Eco-luxury retreat with fjord views and sustainable architecture in UNESCO site. This environmentally conscious development showcases cutting-edge green building technology while preserving the natural beauty of Norway's most spectacular landscape.",
      image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96",
      gallery: [
        "https://images.unsplash.com/photo-1578662996442-48f60103fc96",
        "https://images.unsplash.com/photo-1554114777-4039b6664977"
      ],
      totalInvestment: "€4,500,000",
      nftsRequired: 1125,
      expectedReturn: "20%",
      projectDuration: "30 months",
      status: "Planning Phase",
      highlights: [
        "UNESCO Geiranger Fjord",
        "Sustainable luxury design",
        "Exclusive nature access",
        "Premium eco-tourism market"
      ],
      progress: 10
    }
  ];

  return (
    <section id="projects" className="py-20 bg-gradient-to-b from-gray-800 to-gray-900 relative overflow-hidden">
      {/* Coming Soon Overlay */}
      <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-purple-600/95 to-cyan-600/95 backdrop-blur-sm px-12 py-8 rounded-3xl border-4 border-white/30 shadow-2xl"
        >
          <h3 className="text-5xl md:text-7xl font-bold text-white text-center mb-4">
            COMING SOON
          </h3>
          <p className="text-xl md:text-2xl text-white/90 text-center font-semibold">
            Exciting Investment Opportunities
          </p>
        </motion.div>
      </div>

      <div className="container mx-auto px-6 filter blur pointer-events-none">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-yellow-400 to-blue-400 bg-clip-text text-transparent">
            Upcoming Projects
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Exclusive European real estate investment opportunities available only to Octagon Infinity NFT holders. 
            Each project targets 12-25% annual returns with passive income generation.
          </p>
        </motion.div>

        {/* Project Navigation Tabs - Clean and Fast */}
        <div className="max-w-7xl mx-auto">
          {/* Tab Navigation */}
          <motion.div
            className="flex flex-wrap justify-center gap-3 mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {projects.map((project, index) => (
              <motion.button
                key={project.id}
                onClick={() => {
                  setSelectedProject(index);
                  setSelectedImageIndex(0);
                }}
                className={`group relative px-4 py-3 rounded-xl transition-all duration-300 text-sm lg:text-base font-medium ${
                  selectedProject === index
                    ? 'bg-gradient-to-r from-amber-600 to-blue-600 text-white shadow-lg shadow-amber-500/25'
                    : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/70 hover:text-white border border-gray-700 hover:border-gray-600'
                }`}
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{project.icon}</span>
                  <span>{project.townName}</span>
                </div>
                
                {/* Status indicator */}
                <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${
                  project.status === 'Filling Fast' ? 'bg-red-500' :
                  project.status === 'Starting Soon' ? 'bg-yellow-500' :
                  project.status === 'Development Ready' ? 'bg-green-500' :
                  'bg-blue-500'
                } ${selectedProject === index ? 'opacity-100' : 'opacity-60'}`} />
              </motion.button>
            ))}
          </motion.div>

          {/* Selected Project Details */}
          <div className="relative">
            <motion.div
              key={selectedProject}
              className="grid lg:grid-cols-5 gap-8 items-start"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              {/* Project Header Card */}
              <div className="lg:col-span-5 mb-8">
                <div className="bg-gray-800/30 backdrop-blur-lg rounded-2xl p-6 border border-gray-700/50">
                  <div className="text-center">
                    <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                      {projects[selectedProject].title}
                    </h3>
                    <div className="flex flex-wrap items-center justify-center gap-4 mb-6">
                      <div className="flex items-center space-x-2 text-gray-400">
                        <MapPinIcon className="w-5 h-5" />
                        <span className="text-lg">{projects[selectedProject].location}</span>
                      </div>
                      <div className="w-1 h-1 bg-gray-500 rounded-full hidden sm:block"></div>
                      <div className={`px-4 py-2 rounded-lg text-sm font-medium ${
                        projects[selectedProject].status === 'Filling Fast' ? 'bg-red-500/20 text-red-400' :
                        projects[selectedProject].status === 'Starting Soon' ? 'bg-yellow-500/20 text-yellow-400' :
                        projects[selectedProject].status === 'Development Ready' ? 'bg-green-500/20 text-green-400' :
                        'bg-blue-500/20 text-blue-400'
                      }`}>
                        {projects[selectedProject].status}
                      </div>
                    </div>
                    
                    {/* Quick Stats */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
                      <div className="text-center p-4 bg-gray-800/50 rounded-xl">
                        <div className="text-2xl font-bold text-blue-400 mb-1">{projects[selectedProject].expectedReturn}</div>
                        <div className="text-gray-400 text-sm">Expected Return</div>
                      </div>
                      <div className="text-center p-4 bg-gray-800/50 rounded-xl">
                        <div className="text-2xl font-bold text-amber-400 mb-1">{projects[selectedProject].nftsRequired}</div>
                        <div className="text-gray-400 text-sm">NFTs Required</div>
                      </div>
                      <div className="text-center p-4 bg-gray-800/50 rounded-xl">
                        <div className="text-2xl font-bold text-blue-400 mb-1">{projects[selectedProject].projectDuration}</div>
                        <div className="text-gray-400 text-sm">Duration</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Image Gallery - Left Side (3 columns) */}
              <div className="lg:col-span-3">
                <div className="bg-gray-800/30 backdrop-blur-lg rounded-2xl p-6 border border-gray-700/50">
                  <div className="relative mb-4">
                    <motion.img
                      key={`${selectedProject}-${selectedImageIndex}`}
                      src={projects[selectedProject].gallery[selectedImageIndex]}
                      alt={`${projects[selectedProject].title} ${selectedImageIndex + 1}`}
                      className="w-full h-80 object-cover rounded-xl cursor-pointer"
                      onClick={() => setZoomedImage(projects[selectedProject].gallery[selectedImageIndex])}
                      whileHover={{ scale: 1.01 }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                    
                    {/* Image Navigation Controls */}
                    {projects[selectedProject].gallery.length > 1 && (
                      <>
                        <motion.button
                          onClick={prevImage}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full transition-all duration-200"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <ChevronLeftIcon className="w-4 h-4" />
                        </motion.button>
                        
                        <motion.button
                          onClick={nextImage}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full transition-all duration-200"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <ChevronRightIcon className="w-4 h-4" />
                        </motion.button>
                      </>
                    )}
                  </div>
                  
                  {/* Thumbnail Strip */}
                  <div className="flex justify-center space-x-3">
                    {projects[selectedProject].gallery.map((image, index) => (
                      <motion.button
                        key={index}
                        onClick={() => setSelectedImageIndex(index)}
                        className={`w-16 h-12 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                          selectedImageIndex === index ? 'border-purple-500' : 'border-gray-600 hover:border-gray-500'
                        }`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <img
                          src={image}
                          alt={`${projects[selectedProject].title} ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </motion.button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Project Details - Right Side (2 columns) */}
              <div className="lg:col-span-2">
                <div className="space-y-6">
                  
                  {/* Investment Stats Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-800/30 backdrop-blur-lg rounded-xl p-4 border border-gray-700/50 text-center">
                      <div className="text-2xl font-bold text-amber-400 mb-1">
                        {projects[selectedProject].totalInvestment}
                      </div>
                      <div className="text-gray-400 text-sm">Total Investment</div>
                    </div>
                    
                    <div className="bg-gray-800/30 backdrop-blur-lg rounded-xl p-4 border border-gray-700/50 text-center">
                      <div className="text-2xl font-bold text-blue-400 mb-1">
                        {projects[selectedProject].expectedReturn}
                      </div>
                      <div className="text-gray-400 text-sm">Expected Return</div>
                    </div>
                  </div>

                  {/* Project Description */}
                  <div className="bg-gray-800/30 backdrop-blur-lg rounded-xl p-6 border border-gray-700/50">
                    <p className="text-gray-300 leading-relaxed text-lg">
                      {projects[selectedProject].description}
                    </p>
                  </div>

                  {/* Project Highlights */}
                  <div className="bg-gray-800/30 backdrop-blur-lg rounded-xl p-6 border border-gray-700/50">
                    <h4 className="text-lg font-semibold text-white mb-4">Key Features</h4>
                    <div className="grid grid-cols-1 gap-3">
                      {projects[selectedProject].highlights.map((highlight, index) => (
                        <motion.div
                          key={index}
                          className="flex items-center space-x-3"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                        >
                          <div className="w-2 h-2 bg-gradient-to-r from-amber-500 to-blue-500 rounded-full flex-shrink-0"></div>
                          <span className="text-gray-300">{highlight}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Progress & CTA */}
                  <div className="bg-gray-800/30 backdrop-blur-lg rounded-xl p-6 border border-gray-700/50">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-gray-300 font-medium">Funding Progress</span>
                      <span className="text-amber-400 font-bold">{projects[selectedProject].progress}%</span>
                    </div>
                    <div className="w-full bg-gray-700/50 rounded-full h-2 mb-6">
                      <motion.div
                        className="bg-gradient-to-r from-amber-500 to-blue-500 h-2 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${projects[selectedProject].progress}%` }}
                        transition={{ duration: 1, delay: 0.3 }}
                      />
                    </div>
                    
                    <motion.button
                      className="w-full bg-gradient-to-r from-amber-600 to-blue-600 hover:from-amber-700 hover:to-blue-700 text-white py-4 rounded-xl text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-amber-500/25"
                      whileHover={{ scale: 1.02, y: -1 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => document.getElementById('minting').scrollIntoView({ behavior: 'smooth' })}
                    >
                      Get NFT to Participate
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Image Zoom Modal */}
        {zoomedImage && (
          <motion.div
            className="fixed inset-0 bg-black/90 backdrop-blur-lg z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setZoomedImage(null)}
          >
            <motion.div
              className="relative max-w-4xl max-h-[90vh] w-full"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={zoomedImage}
                alt="Project Image"
                className="w-full h-full object-contain rounded-2xl"
              />
              <motion.button
                onClick={() => setZoomedImage(null)}
                className="absolute top-4 right-4 bg-gray-900/80 hover:bg-gray-800 text-white p-3 rounded-full transition-all duration-200"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default UpcomingProjects;